import {
  AgDataGrid,
  AgDataGridProps,
  PaginationModel,
  useSaladBar,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@mui/material';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { removeAssigneesForList } from '@/apis/lists';
import { TABLE_ROW_HEIGHT } from '@/constants';
import { AssignmentSummary } from '@/types/shared';
import CustomDialog from '@components/CustomDialog';
import { withNonEmptyFilterOperators } from '@/util/gridFilterOperators';
import { useMemo, useState } from 'react';
import { getAssignmentsTableColumnDefinitions } from './columns';

const DASHBOARD_COLUMNS_WITHOUT_EMPTY_FILTERS = new Set([
  'meaningfulInteractionCount',
  'name',
  'dateAssigned',
]);

type AssignmentsTableProps = {
  data: AssignmentSummary[];
  isLoading: boolean;
  paginationModel: PaginationModel;
  onPaginationModelChange?: (model: PaginationModel) => void;
  listName: string;
  listId: number;
} & Partial<AgDataGridProps>;

function AssignmentsTable(props: AssignmentsTableProps) {
  const {
    data,
    isLoading,
    paginationModel,
    onPaginationModelChange = undefined,
    rowCount = undefined,
    listName,
    listId,
  } = props;

  const { getAccessTokenSilently } = useAuth0();
  const { enqueueSuccessNotification } = useSaladBar();
  const queryClient = useQueryClient();

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });

  const [openReleaseDialog, setOpenReleaseDialog] = useState<string | false>(false);

  const columns = useMemo(
    () =>
      getAssignmentsTableColumnDefinitions([
        'Contacts',
        'Meaningful interactions',
        'Assignees',
        'Date assigned',
        'Status',
        'Id',
        'Ellipsis',
      ]).map((column) => {
        if (column.field === 'id') {
          return { ...column, filterable: false };
        }
        if (column.filterOperators) {
          return column;
        }
        if (column.field && DASHBOARD_COLUMNS_WITHOUT_EMPTY_FILTERS.has(column.field)) {
          return withNonEmptyFilterOperators(column);
        }
        return column;
      }),
    []
  );

  const tableData: (AssignmentSummary & {
    listName: string;
    listId: number;
    handleRelease: (id: string) => void;
  })[] = data.map((row) => {
    return {
      ...row,
      handleRelease: (id) => setOpenReleaseDialog(id),
      listName: listName,
      listId: listId,
    };
  });

  // TODO: set skeletons according to the columns passed into the data grid
  const SKELETON_ROWS_WIDTHS = columns.map((x) => x.flex) as number[];
  SKELETON_ROWS_WIDTHS.pop(); // The id column is hidden by default

  return (
    <>
      <AgDataGrid
        loading={isLoading}
        rows={tableData}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 25]}
        skeletonColumnWidths={SKELETON_ROWS_WIDTHS}
        rowCount={rowCount}
        rowHeight={TABLE_ROW_HEIGHT}
        aria-label="Assignments"
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        noRowsMessage={
          'Your list has no assignments. Click on the Assign button below to assign your list'
        }
      />
      <CustomDialog
        dialogTitle="Release contacts?"
        btnSuccessText="Release"
        handleSuccess={async () => {
          if (openReleaseDialog) {
            const token = await getAccessTokenSilently();
            await removeAssigneesForList(token, listId, [openReleaseDialog]);
            enqueueSuccessNotification('Contacts have been released');
            queryClient.invalidateQueries({ queryKey: ['list', listId] });
          }
          setOpenReleaseDialog(false);
        }}
        btnCloseText="Cancel"
        handleClose={() => setOpenReleaseDialog(false)}
        opendialog={openReleaseDialog !== false}
      >
        <Typography>
          Are you sure you want to release all contacts assigned to this user, regardless of whether
          they&apos;ve been contacted?
        </Typography>
      </CustomDialog>
    </>
  );
}

export default AssignmentsTable;
